import { Autocomplete, Box, Stack, styled, TextField } from '@mui/material';
import { ProductTenor } from '@protos/product';
import { TradeLimit } from '@protos/user';
import { OtcExchange, OtcOrderType, OtcOrderTypeDisplayTextMap } from '@protos/v2/otcQuote';
import { useTradableProductSymbolsContext } from '@shared/contexts/TradableProductSymbolsProvider';
import { useMemo } from 'react';
import { TradingPanel } from '../TradingContext';
import { canProductTradeAsLimit } from '../hooks/useTradingForm';

interface TradingFormControlsProps {
  tradingForm: TradingPanel;
  allowedTradingProducts: string[];
  validTenors: ProductTenor[];
  validBackTenors: ProductTenor[];
  userTradeLimits: TradeLimit[];
  onProductChange: (value: string) => void;
  onTenorChange: (value: string, key?: 'left' | 'right') => void;
  onExchangeChange: (value: OtcExchange) => void;
  onOrderTypeChange: (value: OtcOrderType) => void;
  isDisabled?: boolean;
  isValidExchange?: boolean;
}

const TradingAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '100%',
  '& .MuiAutocomplete-input': {
    fontSize: 11,
    padding: '0px 4px !important',
  },
  '& .MuiInputBase-root': {
    height: 20,
    paddingLeft: '1px !important',
    paddingRight: '24px !important',
    borderRadius: 0,
  },

  '& .MuiAutocomplete-endAdornment': {
    right: '2px !important',
    '& .MuiSvgIcon-root': {
      fontSize: 18,
      padding: 0,
      color: theme.palette.text.primary,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    padding: 0,
  },
}));

const autocompleteListboxStyle = {
  fontSize: 11,
  maxHeight: 100,
  borderRadius: '0px !important',
  overflow: 'auto',
};

const autocompleteOptionStyle = {
  fontSize: 11,
  padding: 0,
  paddingLeft: '10px',
};

const isSpreadTenor = (tenor: string | { left: string; right: string }): tenor is { left: string; right: string } => {
  return typeof tenor === 'object' && tenor !== null;
};

const baseAutocompleteProps = {
  size: 'small' as const,
  disableClearable: true,
  disablePortal: true,
  renderInput: (params: any) => <TextField {...params} variant="outlined" tabIndex={-1} />,
  slotProps: {
    listbox: {
      style: autocompleteListboxStyle,
    },
  },
};

export const TradingFormControls = ({
  tradingForm,
  allowedTradingProducts,
  validTenors,
  validBackTenors,
  userTradeLimits,
  onProductChange,
  onTenorChange,
  onExchangeChange,
  onOrderTypeChange,
  isDisabled,
  isValidExchange,
}: TradingFormControlsProps) => {
  const { allProducts } = useTradableProductSymbolsContext();

  const isSpreadProduct = useMemo(() => {
    if (!tradingForm.selectedProduct || !allProducts.length) {
      return false;
    }

    const product = allProducts.find(product => product.symbol && product.symbol.toLowerCase() === tradingForm.selectedProduct.toLowerCase());

    return product?.calendar_type === 'spread';
  }, [tradingForm.selectedProduct, allProducts]);

  const shouldShowLimitOrders = useMemo(() => {
    const product = allProducts.find(p => p.symbol && p.symbol.toLowerCase() === tradingForm.selectedProduct.toLowerCase());
    return canProductTradeAsLimit(userTradeLimits, product);
  }, [tradingForm.selectedProduct, userTradeLimits]);

  const productAutocompleteProps = {
    ...baseAutocompleteProps,
    options: allowedTradingProducts,
    value: tradingForm.selectedProduct.toLocaleUpperCase(),
    getOptionLabel: option => (option as string).toLocaleUpperCase(),
    onChange: (_, newValue) => onProductChange(newValue as string),
    disabled: isDisabled,
    renderOption: (props, option) => (
      <li {...props} key={props.key} style={autocompleteOptionStyle}>
        {(option as string).toLocaleUpperCase()}
      </li>
    ),
  };

  const tenorAutocompleteProps = {
    ...baseAutocompleteProps,
    options: validTenors.map(t => t.code),
    value: tradingForm.selectedTenor,
    getOptionLabel: option => validTenors.find(t => t.code === option)?.display.toLocaleUpperCase() || '',
    onChange: (_, newValue) => onTenorChange(newValue as string),
    disabled: isDisabled,
    renderOption: (props, option) => (
      <li {...props} key={props.key} style={autocompleteOptionStyle}>
        {validTenors.find(t => t.code === option)?.display.toLocaleUpperCase()}
      </li>
    ),
  };

  const spreadTenorAutocompleteProps = {
    ...baseAutocompleteProps,
    options: validTenors.map(t => t.code),
    value: isSpreadTenor(tradingForm.selectedTenor) ? tradingForm.selectedTenor.left : '',
    getOptionLabel: option => validTenors.find(t => t.code === option)?.display.toLocaleUpperCase() || '',
    onChange: (_, newValue) => onTenorChange(newValue as string, 'left'),
    disabled: isDisabled,
    renderOption: (props, option) => (
      <li {...props} key={props.key} style={autocompleteOptionStyle}>
        {validTenors.find(t => t.code === option)?.display.toLocaleUpperCase()}
      </li>
    ),
  };

  const backTenorAutocompleteProps = {
    ...baseAutocompleteProps,
    options: validBackTenors.map(t => t.code),
    value: isSpreadTenor(tradingForm.selectedTenor) ? tradingForm.selectedTenor.right : '',
    getOptionLabel: option => validBackTenors.find(t => t.code === option)?.display.toLocaleUpperCase() || '',
    onChange: (_, newValue) => onTenorChange(newValue as string, 'right'),
    disabled: isDisabled,
    renderOption: (props, option) => (
      <li {...props} key={props.key} style={autocompleteOptionStyle}>
        {validBackTenors.find(t => t.code === option)?.display.toLocaleUpperCase()}
      </li>
    ),
  };

  const exchangeAutocompleteProps = {
    ...baseAutocompleteProps,
    options: userTradeLimits.find(limit => limit.symbol === tradingForm.selectedProduct)?.exchange || [],
    value: tradingForm.selectedExchange,
    onChange: (_, newValue) => onExchangeChange(newValue as OtcExchange),
    getOptionLabel: option => option.toLocaleUpperCase(),
    disabled: isDisabled,
    renderOption: (props, option) => (
      <li {...props} key={props.key} style={autocompleteOptionStyle}>
        {(option as OtcExchange).toLocaleUpperCase()}
      </li>
    ),
  };

  const orderTypeAutoCompleteProps = {
    ...baseAutocompleteProps,
    options: [OtcOrderType.fill_or_kill, OtcOrderType.limit],
    getOptionLabel: (option: any) => {
      return OtcOrderTypeDisplayTextMap.get(option) || '';
    },
    value: tradingForm.selectedOrderType,
    disabled: isDisabled,
    disableClearable: true,
    autoSelect: true,
    renderOption: (props, option: any) => {
      return (
        <li {...props} key={props.key} style={autocompleteOptionStyle}>
          {OtcOrderTypeDisplayTextMap.get(option) || ''}
        </li>
      );
    },
    renderInput: params => {
      return <TextField {...params} />;
    },
    onChange: (_, newValue) => {
      onOrderTypeChange(newValue as OtcOrderType);
    },
  };

  const renderForm = useMemo(() => {
    if (!isValidExchange) {
      return (
        <Stack display="flex" gap={0.5} width="100%">
          <TradingAutocomplete {...productAutocompleteProps} />
          <TradingAutocomplete {...exchangeAutocompleteProps} />
          {shouldShowLimitOrders && <TradingAutocomplete {...orderTypeAutoCompleteProps} />}
        </Stack>
      );
    } else if (isSpreadProduct) {
      return (
        <Stack display="flex" gap={0.5} width="100%">
          <TradingAutocomplete {...productAutocompleteProps} />
          <Box display="flex" width="100%">
            <TradingAutocomplete {...spreadTenorAutocompleteProps} />
            <TradingAutocomplete {...backTenorAutocompleteProps} />
          </Box>
          <TradingAutocomplete {...exchangeAutocompleteProps} />
          {shouldShowLimitOrders && <TradingAutocomplete {...orderTypeAutoCompleteProps} />}
        </Stack>
      );
    } else {
      return (
        <Stack display="flex" gap={0.5} width="100%">
          <TradingAutocomplete {...productAutocompleteProps} />
          <TradingAutocomplete {...tenorAutocompleteProps} />
          <TradingAutocomplete {...exchangeAutocompleteProps} />
          {shouldShowLimitOrders && <TradingAutocomplete {...orderTypeAutoCompleteProps} />}
        </Stack>
      );
    }
  }, [
    isValidExchange,
    isSpreadProduct,
    productAutocompleteProps,
    tenorAutocompleteProps,
    spreadTenorAutocompleteProps,
    backTenorAutocompleteProps,
    exchangeAutocompleteProps,
  ]);

  return renderForm;
};
